import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";

import { OnboardingState } from "../../../../types/onboarding";
import { nextPipelineStates, stateLabel } from "../../../../utils/onboardingStates";

interface AdvanceStateDialogProps {
  visible: boolean;
  currentState: OnboardingState;
  onHide: () => void;
  onConfirm: (target: OnboardingState) => Promise<void> | void;
}

/**
 * US2: advance a prospect to a later pipeline state. Only forward states are
 * offered (FR-010); the backend rejects anything illegal.
 */
const AdvanceStateDialog: React.FC<AdvanceStateDialogProps> = ({
  visible,
  currentState,
  onHide,
  onConfirm,
}) => {
  const [target, setTarget] = useState<OnboardingState | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const options = nextPipelineStates(currentState).map((s) => ({
    label: stateLabel(s),
    value: s,
  }));

  const submit = async () => {
    if (!target) return;
    setSubmitting(true);
    try {
      await onConfirm(target);
      setTarget(null);
      onHide();
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button
        label="Advance"
        icon="pi pi-arrow-right"
        onClick={submit}
        disabled={!target || submitting}
        loading={submitting}
      />
    </div>
  );

  return (
    <Dialog
      header="Advance status"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "30rem" }}
    >
      <p className="mt-0">
        Move this prospect forward to a later step. This is recorded in the
        status history.
      </p>
      <Dropdown
        value={target}
        options={options}
        onChange={(e) => setTarget(e.value)}
        placeholder="Select the next step"
        className="w-full"
        emptyMessage="No further steps available."
      />
    </Dialog>
  );
};

export default AdvanceStateDialog;
